import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek } from "date-fns";
import { TasksStatus, TasksTimeframe } from "../../../../models/enums/enums";
import { AgreementUserRole, UserTasksConnectionWhere, UserWhere } from "../../../../models/generated/graphql";
import { isEmptyValue } from "../../../../utils/is-empty-value";
import { NotificationsType } from "apps/platform-client/client/src/models/common/notifications";

export function getBasicUserFilter(userId: string, notificationIds: number[]) {
	return {
		id: userId,
		tasks: {
			triggerEventId_IN: notificationIds,
			OR: [
				{
					triggerEventId_IN: [
						NotificationsType.RefundInvoiceReceiverConfirmation,
						NotificationsType.RefundInvoiceReceiverApproval,
					],
					document: {
						refunds: {
							OR: [
								{
									case: {
										baseAgreement: {
											isDeleted: false,
											userRolesConnection: {
												edge: { role_IN: [AgreementUserRole.Owner, AgreementUserRole.Approver] },
												node: { id: userId },
											},
										},
									},
								},
								{
									agreement: {
										isDeleted: false,
										isCaseless: true,
										userRolesConnection: {
											edge: { role_IN: [AgreementUserRole.Owner, AgreementUserRole.Approver] },
											node: { id: userId },
										},
									},
								},
							],
						},
					},
				},
				{
					triggerEventId_NOT_IN: [
						NotificationsType.RefundInvoiceReceiverConfirmation,
						NotificationsType.RefundInvoiceReceiverApproval,
					],
					document: {
						refunds: {
							OR: [
								{
									case: {
										baseAgreement: {
											isDeleted: false,
											userRolesConnection: {
												edge: {
													role_IN: [
														AgreementUserRole.Owner,
														AgreementUserRole.Approver,
														AgreementUserRole.Editor,
													],
												},
												node: { id: userId },
											},
										},
									},
								},
								{
									agreement: {
										isCaseless: true,
										isDeleted: false,
										userRolesConnection: {
											edge: {
												role_IN: [AgreementUserRole.Owner, AgreementUserRole.Approver, AgreementUserRole.Editor],
											},
											node: { id: userId },
										},
									},
								},
							],
						},
					},
				},
				{
					case: {
						baseAgreement: {
							isDeleted: false,
							userRolesConnection: {
								edge: { role_NOT_IN: [AgreementUserRole.LimitedAccessUser, AgreementUserRole.FullAccessUser] },
								node: { id: userId },
							},
						},
					},
				},
				{
					agreement: {
						isDeleted: false,
						userRolesConnection: {
							edge: { role_NOT_IN: [AgreementUserRole.LimitedAccessUser, AgreementUserRole.FullAccessUser] },
							node: { id: userId },
						},
					},
				},
			],
		},
	} as UserWhere;
}

export function getBasicTaskFilter(notificationIds: number[]) {
	return {
		node: { triggerEventId_IN: notificationIds },
	} as UserTasksConnectionWhere;
}

export function getIntervalTimeframe(value: TasksTimeframe) {
	let currentTime = new Date();

	switch (value) {
		case TasksTimeframe.Today:
			return [startOfDay(currentTime).getTime(), endOfDay(currentTime).getTime()];
		case TasksTimeframe.ThisWeek:
			return [startOfWeek(currentTime, { weekStartsOn: 1 }).getTime(), endOfWeek(currentTime, { weekStartsOn: 1 }).getTime()];
		default:
			return [startOfMonth(currentTime).getTime(), endOfMonth(currentTime).getTime()];
	}
}

export function setAssignToFilters(assignedToMeFilter: any, userId: string, userTaskFilter: UserTasksConnectionWhere) {
	const taskFilter = {
		...userTaskFilter,
		OR: assignedToMeFilter
			? [
					{
						node: {
							document: {
								assignedTo: { id: userId },
							},
						},
					},
					{
						node: {
							agreement: {
								userRolesConnection: {
									edge: {
										isAssignee: true,
									},
									node: { id: userId },
								},
							},
						},
					},
			  ]
			: undefined,
	} as UserTasksConnectionWhere;

	return taskFilter;
}
export function setTimeframeFilters(timeFrameFilter: any, taskFilter: UserTasksConnectionWhere): UserTasksConnectionWhere {
	if (!timeFrameFilter || timeFrameFilter === TasksTimeframe.AnyTime) return taskFilter;
	const [startIntervalDate, endIntervalDate] = getIntervalTimeframe(timeFrameFilter!);

	return {
		...taskFilter,
		node: {
			...taskFilter.node,
			timeStamp_GTE: startIntervalDate,
			timeStamp_LTE: endIntervalDate,
		},
	} as UserTasksConnectionWhere;
}

export function searchItem(searchItem: string, taskFilter: UserTasksConnectionWhere, preferredLanguage: string): UserTasksConnectionWhere {
	const trimmedSearchValue = searchItem.trim().toLowerCase();
	if (isEmptyValue(searchItem)) return taskFilter;
	return {
		...taskFilter,
		node: {
			...taskFilter.node,
			...(preferredLanguage === "en-US"
				? { taskContentEn_CONTAINS: trimmedSearchValue }
				: { taskContentDe_CONTAINS: trimmedSearchValue }),
		},
	} as UserTasksConnectionWhere;
}

export function setStatusFilters(tasksStatusFilter: any, taskFilter: UserTasksConnectionWhere): UserTasksConnectionWhere {
	if (!tasksStatusFilter || tasksStatusFilter === TasksStatus.AnyStatus) return taskFilter;

	return {
		...taskFilter,
		edge: { ...taskFilter.edge, isCompleted: tasksStatusFilter === TasksStatus.Closed },
	} as UserTasksConnectionWhere;
}
